/**
 * Checkbox — caixa de seleção do design system Pharu.
 *
 * Substitui o `<input type="checkbox">` nativo (que sai com a cara do
 * sistema operacional) por uma caixa desenhada com os tokens do tema:
 * borda `--border`, fundo `--surface`, marcada em `--accent` com o check
 * branco, estado indeterminado (traço) para "selecionar tudo" parcial,
 * anel de foco em `--accent-soft` e hover que acende a borda.
 *
 * É o próprio `<input>`: aceita `checked`, `onChange`, `readOnly`,
 * `disabled`, `aria-label`, `ref` e `indeterminate` (via ref) como sempre.
 */
import styled from "styled-components";

import { mq } from "../../styles/canonical/breakpoints";

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 0;
  border-radius: 5px;
  border: 1.5px solid var(--border-strong, var(--border));
  background: var(--surface);
  display: inline-grid;
  place-items: center;
  cursor: pointer;
  vertical-align: middle;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  &::after {
    content: "";
    width: 10px;
    height: 10px;
    opacity: 0;
    transform: scale(0.6);
    transition: opacity 0.12s ease, transform 0.12s ease;
    background: #fff;
    /* Check desenhado em máscara: escala com a caixa e não depende de fonte. */
    -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path d='M2.2 6.4l2.5 2.5 5.1-5.6' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'/></svg>") center / contain no-repeat;
    mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path d='M2.2 6.4l2.5 2.5 5.1-5.6' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'/></svg>") center / contain no-repeat;
  }

  &:hover:not(:disabled) {
    border-color: var(--accent);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }

  &:checked,
  &:indeterminate {
    background: var(--accent);
    border-color: var(--accent);
  }

  &:checked::after {
    opacity: 1;
    transform: scale(1);
  }

  &:indeterminate::after {
    opacity: 1;
    transform: scale(1);
    -webkit-mask: none;
    mask: none;
    width: 9px;
    height: 2px;
    border-radius: 2px;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  /* Auditoria mobile 2026-09-24 (S5): 18px era o pior alvo da lista — a
     pessoa marcava a linha errada sem perceber. O desenho vai a 22px; a
     ÁREA de toque de 44px vem do wrapper de quem usa em card/lista
     (var(--tap-min)), não daqui — um input não estica sem deformar. */
  @media ${mq.mobile} {
    width: 22px;
    height: 22px;
    &::after { width: 12px; height: 12px; }
    &:indeterminate::after { width: 11px; height: 2px; }
  }
`;

export default Checkbox;
